/**
 * React Query Hooks for Sanity Guidance Content
 *
 * Provides data fetching hooks for guidance content from Sanity CMS.
 * Falls back to API-based content when Sanity is not configured.
 */

import { useQuery } from '@tanstack/react-query';
import {
  isSanityConfigured,
  fetchGuidanceList,
  fetchGuidanceByTopic,
  fetchAllGuidance,
  fetchGuidanceByCategory,
  type SanityGuidance,
} from '@/lib/sanity';
import { getGuidance as getApiGuidance } from '@/api/values';

// Query keys
export const guidanceKeys = {
  all: ['guidance'] as const,
  lists: () => [...guidanceKeys.all, 'list'] as const,
  list: (category: string) => [...guidanceKeys.lists(), category] as const,
  details: () => [...guidanceKeys.all, 'detail'] as const,
  detail: (topic: string) => [...guidanceKeys.details(), topic] as const,
  byCategory: () => [...guidanceKeys.all, 'byCategory'] as const,
};

/**
 * Hook to fetch all guidance for a category
 */
export function useGuidanceList(category: string) {
  return useQuery({
    queryKey: guidanceKeys.list(category),
    queryFn: () => fetchGuidanceList(category),
    enabled: isSanityConfigured,
    staleTime: 10 * 60 * 1000, // 10 minutes - content doesn't change often
  });
}

/**
 * Hook to fetch single guidance by topic
 */
export function useGuidanceByTopic(topic: string) {
  return useQuery({
    queryKey: guidanceKeys.detail(topic),
    queryFn: () => fetchGuidanceByTopic(topic),
    enabled: isSanityConfigured && !!topic,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch all guidance content
 */
export function useAllGuidance() {
  return useQuery({
    queryKey: guidanceKeys.all,
    queryFn: () => fetchAllGuidance(),
    enabled: isSanityConfigured,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch guidance grouped by category
 */
export function useGuidanceByCategory() {
  return useQuery({
    queryKey: guidanceKeys.byCategory(),
    queryFn: () => fetchGuidanceByCategory(),
    enabled: isSanityConfigured,
    staleTime: 10 * 60 * 1000,
  });
}

// =============================================================================
// Fallback Hooks (for when Sanity is not configured)
// =============================================================================

/**
 * Legacy guidance format from API
 */
interface LegacyGuidanceContent {
  title: string;
  content: string;
}

/**
 * Hook to fetch guidance with fallback to API
 * This maintains backward compatibility with the existing API-based system
 */
export function useGuidanceWithFallback(category: string = 'values') {
  // Try Sanity first
  const sanityQuery = useGuidanceList(category);

  // Fallback to API if Sanity not configured
  const apiQuery = useQuery({
    queryKey: ['guidance-api', category],
    queryFn: () => getApiGuidance(),
    enabled: !isSanityConfigured,
    staleTime: 10 * 60 * 1000,
  });

  // If Sanity is configured, use Sanity data
  if (isSanityConfigured) {
    return {
      data: sanityQuery.data,
      isLoading: sanityQuery.isLoading,
      error: sanityQuery.error,
      isSanity: true,
    };
  }

  // Transform API data to match component expectations
  const transformedData = apiQuery.data?.guidance
    ? transformApiGuidance(apiQuery.data.guidance as Record<string, LegacyGuidanceContent>)
    : undefined;

  return {
    data: transformedData,
    isLoading: apiQuery.isLoading,
    error: apiQuery.error,
    isSanity: false,
  };
}

/**
 * Transform legacy API guidance to Sanity-like format
 */
function transformApiGuidance(
  apiGuidance: Record<string, LegacyGuidanceContent>
): SanityGuidance[] {
  const iconMap: Record<string, string> = {
    introduction: 'book',
    identifying_values: 'lightbulb',
    setting_weights: 'target',
    maintaining_alignment: 'refresh-cw',
  };

  return Object.entries(apiGuidance).map(([topic, content]) => ({
    _id: topic,
    title: content.title,
    slug: topic,
    topic,
    category: 'values',
    summary: content.content.split('\n')[0].substring(0, 100),
    icon: iconMap[topic] || 'book',
    // Convert plain text to simple portable text structure
    content: [
      {
        _key: 'content-block',
        _type: 'block',
        style: 'normal',
        children: content.content.split('\n\n').map((paragraph, i) => ({
          _key: `para-${i}`,
          _type: 'span',
          text: paragraph,
        })),
      },
    ],
    quickActions: [],
  }));
}

/**
 * Check if Sanity CMS is available
 */
export function useSanityStatus() {
  return {
    isConfigured: isSanityConfigured,
    message: isSanityConfigured
      ? 'Using Sanity CMS for guidance content'
      : 'Sanity not configured - using API fallback',
  };
}

